/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    Trash2, Loader2, AlertCircle,
    User, Phone, Mail, MessageSquare, X, Eye, Calendar
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL_NEW;

export default function AdminContacts() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchContacts = async () => {
            const token = localStorage.getItem('admin_token');

            if (!token) {
                setError(
                    <div className="text-center space-y-6">
                        <div className="text-xl font-semibold text-gray-800">
                            You need to be logged in to view this page
                        </div>
                        <button
                            onClick={() => window.location.href = '/admin/auth/login'}
                            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-md transition-all transform hover:scale-105"
                        >
                            <span>Go to Login Page</span>
                        </button>
                    </div>
                );
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE}/contacts`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Expecting { data: [ ... ] } or just [ ... ] depending on API wrapper.
                // Based on Referrals: response.data.data
                setContacts(response.data.data || response.data || []);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load contacts');
            } finally {
                setLoading(false);
            }
        };

        fetchContacts();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this contact request?')) return;

        const token = localStorage.getItem('admin_token');
        setDeletingId(id);

        try {
            await axios.delete(`${API_BASE}/contacts/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setContacts(prev => prev.filter(c => c.id !== id));
            toast.success('Contact request deleted successfully');
            if (selectedContact?.id === id) setSelectedContact(null);
        } catch (err) {
            toast.error('Failed to delete contact request');
        } finally {
            setDeletingId(null);
        }
    };

    const openDetailsModal = (contact) => {
        setSelectedContact(contact);
    };

    const closeModal = () => {
        setSelectedContact(null);
    };

    // Pagination Logic
    const totalPages = Math.ceil(contacts.length / itemsPerPage);
    const paginatedContacts = contacts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    if (loading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-12 h-12 animate-spin text-blue-600" /></div>;

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50 p-6 rounded-xl border border-dashed border-red-200">
                <AlertCircle className="w-16 h-16 text-red-500 mb-6" />
                <div className="text-gray-800 text-lg">
                    {typeof error === 'string' ? error : error}
                </div>
            </div>
        );
    }

    return (
        <div className="">
            <Toaster position="top-right" richColors closeButton />

            <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Contact Requests</h2>
                <p className="text-gray-600">Manage incoming messages from the contact form</p>
            </div>

            {/* Table */}
            <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact Info</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Message Preview</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginatedContacts.length > 0 ? (
                                paginatedContacts.map((contact) => (
                                    <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{contact.full_name}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Mail className="w-3 h-3 text-gray-400" />
                                                <span className="text-gray-700">{contact.email}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Phone className="w-3 h-3 text-gray-400" />
                                                <span className="text-gray-500">{contact.phone}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                            {contact.message}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {new Date(contact.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openDetailsModal(contact)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(contact.id)}
                                                    disabled={deletingId === contact.id}
                                                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
                                                    title="Delete"
                                                >
                                                    {deletingId === contact.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center">
                                            <div className="bg-gray-100 p-4 rounded-full mb-3">
                                                <MessageSquare className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <p className="text-lg font-medium">No contact requests found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t flex justify-between items-center bg-gray-50">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Custom Details Modal */}
            <AnimatePresence>
                {selectedContact && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Message Details</h2>
                                    <p className="text-sm text-gray-500 mt-1">ID: #{selectedContact.id}</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-6 h-6 text-gray-500" />
                                </button>
                            </div>

                            <div className="p-8 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <User className="w-5 h-5 text-gray-500" />
                                            Sender Info
                                        </h3>
                                        <div className="space-y-3 pl-1">
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Full Name</span>
                                                <span className="text-base text-gray-900">{selectedContact.full_name}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Recieved Date</span>
                                                <span className="text-base text-gray-900 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    {new Date(selectedContact.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                            <Phone className="w-5 h-5 text-gray-500" />
                                            Contact Info
                                        </h3>
                                        <div className="space-y-3 pl-1">
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Email Address</span>
                                                <a href={`mailto:${selectedContact.email}`} className="text-base text-blue-600 hover:underline">{selectedContact.email}</a>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Phone Number</span>
                                                <a href={`tel:${selectedContact.phone}`} className="text-base text-blue-600 hover:underline">{selectedContact.phone}</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4 pt-4">
                                    <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-900 border-b pb-2">
                                        <MessageSquare className="w-5 h-5 text-gray-500" />
                                        Message
                                    </h3>
                                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 text-gray-800 leading-relaxed whitespace-pre-wrap">
                                        {selectedContact.message}
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t px-8 py-5 flex justify-end gap-3">
                                <button
                                    onClick={() => handleDelete(selectedContact.id)}
                                    className="px-6 py-2.5 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition border border-red-200"
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition shadow-lg shadow-gray-200"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

// 